import { useFacilityContext } from '@aspendental/shared-components-web';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Alert, Button, CircularProgress, FormControlLabel, Grid, Stack, Switch, Typography, useMediaQuery, useTheme } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import en from 'date-fns/locale/en-US';
import es from 'date-fns/locale/es';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { REASON_FOR_VISIT_ID } from '../ReasonForVisit/ReasonForVisit.helpers';
import { FilterBuilder, SIXTY_DAYS, getLead, groupSlotsByDate, renderSlots, type Filters, type Lead } from './DateAndTime.helpers';
import FilterDialog from './FilterDialog';
import FilterDrawer from './FilterDrawer';
import Location from './Location';
import LocationSelect from './LocationSelect';
import NearbySlots from './NearbySlots';
import PhoneNumberLink from '@src/components/PhoneNumberLink';
import { DataLayerStepNames } from '@src/constants';
import { useLanguagePreferenceTracking } from '@src/hooks/useLanguagePreferenceTracking';
import { Application } from '@src/lib/__generated/api';
import { useGetNearbySlots } from '@src/services/useGetNearbySlots';
import { useGetSlots } from '@src/services/useGetSlots';
import { useAppStore, useFacilityStore, useFormSectionStore, usePatientInformationFormStore, useReasonForVisitStore } from '@src/store';
import useNeedsBasedQuestionsStore from '@src/store/modules/needs-based-questions.store';
import { fireDataLayerEvent } from '@src/utils/data-layer-utils';
import { sessionStorageHelper } from '@src/utils/session-storage-utils';
export default function DateAndTime() {
  const [date, setDate] = useState<Date>(new Date());
  const [days, setDays] = useState(6);
  const [threeDaySwitch, setThreeDaySwitch] = useState(false);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    timeOfDay: '',
    weekDays: []
  });
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [timeOfDay, setTimeOfDay] = useState('');
  const [weekDays, setWeekDays] = useState<string[]>([]);
  const [lead, setLead] = useState<Lead | null>(null);
  const facility = useFacilityStore(store => store.facility);
  const digitalVisitReason = useReasonForVisitStore(store => store.reasonForVisit);
  const firstName = usePatientInformationFormStore(store => store.firstName);
  const lastName = usePatientInformationFormStore(store => store.lastName);
  const patientAge = usePatientInformationFormStore(store => store.patientAge());
  const mottoFlow = useAppStore(store => store.mottoFlow);
  const isDenturesFlow = useAppStore(store => store.denturesFlow);
  const dateOfBirthString = usePatientInformationFormStore(store => store.dateOfBirthString());
  const email = usePatientInformationFormStore(store => store.email);
  const phonenumber = usePatientInformationFormStore(store => store.phonenumber);
  const setActiveSection = useFormSectionStore(store => store.setActiveSection);
  const {
    reasonForVisit
  } = useReasonForVisitStore(store => store);
  const {
    needsBasedAnswers,
    questionnaireId
  } = useNeedsBasedQuestionsStore(store => store);
  const webFacilityDetails = useFacilityContext();
  const router = useRouter();
  const {
    locale
  } = router;
  const localeForAdp = locale === 'en-US' ? en : es;
  const {
    t
  } = useTranslation('dateAndTime');
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const datePickerToday = new Date();
  const startDate = new Date().toISOString().slice(0, 10);
  const today = new Date();
  const endDate = new Date(today.setDate(today.getDate() + SIXTY_DAYS)).toISOString().slice(0, 10);
  const threeDays = 3;
  const initialDays = isSmallScreen ? 8 : 6;
  const sixDays = 6;
  const nearbyToday = new Date();

  // adds an additional check to ensure at least one value in the object is non-empty
  const hasValidAnswers = needsBasedAnswers && Object.values(needsBasedAnswers).some(value => Array.isArray(value) ? value.length > 0 : value !== '');
  const params = {
    startDate,
    endDate,
    facilityCode: facility?.facilityCode,
    application: Application.AspenDental,
    digitalVisitReason,
    ...(firstName && {
      firstName
    }),
    ...(lastName && {
      lastName
    }),
    ...(questionnaireId.length && {
      questionnaireId: questionnaireId
    }),
    ...(hasValidAnswers && {
      questionnaireAnsweredQuestions: needsBasedAnswers
    }),
    ...(dateOfBirthString.length && {
      dateOfBirth: dateOfBirthString
    }),
    ...(email && {
      email
    }),
    ...(phonenumber && {
      phone: phonenumber
    })
  };
  const {
    data: slots,
    isPending: isSlotsPending
  } = useGetSlots(params);
  const {
    data: nearbySlots,
    isPending: isNearbySlotsPending
  } = useGetNearbySlots({
    ...params,
    ...(threeDaySwitch && {
      endDate: new Date(nearbyToday.setDate(nearbyToday.getDate() + 2)).toISOString().slice(0, 10)
    })
  });
  const filteredSlots = useMemo(() => groupSlotsByDate(FilterBuilder.addData(slots ?? []).filterByDayOfWeek(filters.weekDays).filterByTimeOfDay(filters.timeOfDay).filterByDaysAfterDate(date).filterByDaysFromToday(threeDaySwitch ? threeDays : null).build()), [slots, filters.timeOfDay, filters.weekDays, date, threeDaySwitch]);

  // setting the session storage key to false for the patient information step to prevent multiple data layer events
  sessionStorageHelper.setKey(DataLayerStepNames.timeSelection, 'false');
  useEffect(() => {
    if (!isSlotsPending && !isNearbySlotsPending && lead === null) {
      const leadScore = getLead(slots, nearbySlots);
      if (leadScore) {
        setLead(leadScore);
      }
    }
  }, [isSlotsPending, isNearbySlotsPending, slots, nearbySlots, lead]);
  useEffect(() => {
    if (webFacilityDetails?.officeInfo?.code && webFacilityDetails?.officeInfo?.name && lead !== null) {
      //this method is added to prevent multiple data layer events from being fired in mobile view
      //pass the step name as first parameter
      fireDataLayerEvent(DataLayerStepNames.timeSelection, {
        language_preference: locale,
        brand: mottoFlow ? 'mottoaligners' : 'aspendental',
        lead_score: lead.leadScore,
        lead_tier_national: lead.leadTierNational,
        lead_tier_office: lead.leadTierOffice,
        step_name: DataLayerStepNames.timeSelection,
        reason_for_visit: reasonForVisit,
        reason_for_visit_id: REASON_FOR_VISIT_ID[reasonForVisit],
        website_selected_facility_id: webFacilityDetails?.officeInfo?.code,
        website_selected_facility_name: webFacilityDetails?.officeInfo?.name
      });
    }
  }, [reasonForVisit, webFacilityDetails?.officeInfo?.code, webFacilityDetails?.officeInfo?.name, mottoFlow, lead]);
  useEffect(() => {
    setDays(initialDays);
    setThreeDaySwitch(false);
  }, [setDays, facility, initialDays]);
  const handleDatePickerOpen = () => {
    if (isSmallScreen) {
      setOpenDrawer(true);
      setOpenDatePicker(false);
    } else {
      setOpenDrawer(false);
      setOpenDatePicker(true);
    }
  };
  useEffect(() => {
    if (!isSlotsPending && (filteredSlots && Object.keys(filteredSlots).length === 0 || slots?.length === 0)) {
      fireDataLayerEvent(DataLayerStepNames.timeSelection, {
        language_preference: locale,
        brand: mottoFlow ? 'mottoaligners' : 'aspendental',
        event: 'no_appointments_available_msg',
        step_name: DataLayerStepNames.timeSelection,
        reason_for_visit: reasonForVisit,
        reason_for_visit_id: REASON_FOR_VISIT_ID[reasonForVisit],
        website_selected_facility_id: webFacilityDetails?.officeInfo?.code,
        website_selected_facility_name: webFacilityDetails?.officeInfo?.name
      });
    }
  }, [isSlotsPending, slots, reasonForVisit, webFacilityDetails?.officeInfo?.code, webFacilityDetails?.officeInfo?.name, mottoFlow, locale, filteredSlots]);
  useLanguagePreferenceTracking({
    stepName: DataLayerStepNames.timeSelection,
    webFacilityDetails,
    brand: mottoFlow ? 'mottoaligners' : 'aspendental'
  });
  return <>
			{!patientAge && !mottoFlow && !isDenturesFlow ? <Alert severity="error" variant="flatError">
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Typography color="text.secondary" sx={{
            display: 'flex'
          }}>
								{t('dobRequired')}
							</Typography>
						</Grid>
						<Grid item xs={12}>
							<Button variant="outlined" color="primary" onClick={() => setActiveSection(0)}>
								{t('update')}
							</Button>
						</Grid>
					</Grid>
				</Alert> : null}
			{(patientAge || mottoFlow || isDenturesFlow) && <>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={5}>
							<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeForAdp}>
								<DatePicker label={t('calendar')} name="selectADate" value={date} minDate={new Date()} maxDate={new Date(datePickerToday.setDate(datePickerToday.getDate() + SIXTY_DAYS))} shouldDisableDate={date => date.getDay() === 0} onChange={newValue => {
              setDate(newValue ?? new Date());
              setThreeDaySwitch(false);
            }} open={openDatePicker} onOpen={handleDatePickerOpen} onClose={() => setOpenDatePicker(false)} />
								<FilterDrawer openDrawer={openDrawer} handleDrawerClose={() => setOpenDrawer(false)} filters={filters} setOpenDrawer={setOpenDrawer} setDate={setDate} setThreeDaySwitch={setThreeDaySwitch} date={date} threeDaySwitch={threeDaySwitch} weekDays={weekDays} timeOfDay={timeOfDay} setTimeOfDay={setTimeOfDay} setWeekDays={setWeekDays} setFilters={setFilters} />
							</LocalizationProvider>
						</Grid>
						<Grid item xs={12} sm={5}>
							<LocationSelect />
						</Grid>
						<Grid item sm={1} md={2} sx={{
          display: {
            xs: 'none',
            sm: 'flex'
          }
        }}>
							<Button variant="secondaryDefault" size="S" startIcon={<FilterListIcon />} onClick={() => setOpenFilterDialog(true)} data-test-id="button_filter_slots">
								{t('filter')}
							</Button>
							<FilterDialog openFilterDialog={openFilterDialog} setOpenFilterDialog={setOpenFilterDialog} setFilters={setFilters} filters={filters} setThreeDaySwitch={setThreeDaySwitch} threeDaySwitch={threeDaySwitch} setTimeOfDay={setTimeOfDay} setWeekDays={setWeekDays} weekDays={weekDays} timeOfDay={timeOfDay} />
						</Grid>
					</Grid>
					<FormControlLabel sx={{
        mt: 2
      }} control={<Switch checked={threeDaySwitch} onChange={() => {
        setDate(new Date());
        setThreeDaySwitch(prevState => !prevState);
        setFilters({
          timeOfDay: '',
          weekDays: []
        });
        setDays(initialDays);
      }} />} label={<Typography variant="bodyMediumBook">{t('showAppts3Days')}</Typography>} />
				</>}
			{facility ? <Location location={facility} primaryLocation>
					{isSlotsPending ? <Stack direction="row" justifyContent="center">
							<CircularProgress color="secondary" />
						</Stack> : filteredSlots ? <>
							{renderSlots(filteredSlots, days, facility, filters)}
							{Object.keys(filteredSlots ?? {}).length === 0 ? <Alert icon={false} variant="flatPrimary">
									<Typography variant="bodyMediumBook" color="text.secondary">
										{t('noApptsAvailable')}{' '}
										<PhoneNumberLink phoneNumber={facility.phone ?? ''} variant="bodyMediumBook" />
									</Typography>
								</Alert> : <>
									{Object.keys(filteredSlots ?? {}).length > days && !threeDaySwitch ? isSmallScreen ? <Grid container justifyContent="center" alignItems="center">
												<Typography variant="bodyMediumBook">
													{t('needMoreAvaialability')}
												</Typography>
												<Button size="M" variant="tertiaryDefault" onClick={() => window.scrollTo(0, 0)}>
													{t('backToCalendar')}
												</Button>
											</Grid> : <Grid container justifyContent="center">
												<Button variant="tertiaryDefault" size="M" onClick={() => setDays(prev => prev + sixDays)}>
													{t('loadMore')}
												</Button>
											</Grid> : null}
								</>}
						</> : null}
				</Location> : null}
			{nearbySlots?.length ? <>
					<Typography variant="header3" mb={2}>
						{t('apptsNearby')}
					</Typography>
					<Stack spacing={2}>
						{nearbySlots.map(slot => {
          return <NearbySlots key={slot.facilityCode} nearbySlots={slot} filters={filters} date={date} />;
        })}
					</Stack>
				</> : null}
		</>;
}